// Importing necessary modules and components from Material-UI
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from "@mui/material";

// Importing chart components and data

// Importing utility functions and models
import { HistoricalEmissionData } from "object-models/HistoricalEmissionData";
import { LocationModel } from "object-models/location-model";
import { _updateLocation } from "middlewares/LocationApi/location";

// Importing loading spinner component
import Spinner from "components/Spinner";

// Importing static data for base and target years
import { targetYears } from "pages/TargetForm/data/TargetFormData";
import { BAUGHGEmissions } from "./data/BAUGHGEmissions";
import { AddGrowthRateProps } from "./data/AddGrowthRateProps";
import { LineChart } from "./LineChart";
import { AlertsProps } from "components/Alerts/data/Alerts.type";
import SnackBar from "components/SnackBar";
import { Constants } from "constants/constant";
import TitleDefinition from "components/TitleDefinition";
import {
  getFriendlyErrorMessage,
  reportUiError,
} from "services/ui-error-reporter";

const calculateLinearProjectedEmissions = (
  baselineTotalEmissions: number,
  growthRate: number,
  yearsFromBase: number
) =>
  Number(
    (
      baselineTotalEmissions *
      (1 + growthRate * yearsFromBase)
    ).toFixed(2)
  );

const getTotalEmissions = (emissionData?: {
  scope1GHGEmission?: number | null;
  scope2GHGEmission?: number | null;
  scope3GHGEmission?: number | null;
}) =>
  (emissionData?.scope1GHGEmission ?? 0) +
  (emissionData?.scope2GHGEmission ?? 0) +
  (emissionData?.scope3GHGEmission ?? 0);

const getLatestHistoricalBaseline = (
  historicalData: HistoricalEmissionData[]
): HistoricalEmissionData | null => {
  if (!historicalData.length) {
    return null;
  }

  return historicalData.reduce((latest, current) => {
    if (!latest) {
      return current;
    }

    return current.emissionYear > latest.emissionYear ? current : latest;
  }, historicalData[0]);
};

/**
 * AddGrowthRate Component
 * @param {AddGrowthRateProps} props - Props for the AddGrowthRate component
 * @returns {JSX.Element} - The rendered component
 */
const AddGrowthRate = (props: AddGrowthRateProps) => {
  // Constants and initializations
  const initialLocationDetails: LocationModel = {
    ...(props.locationDetails ?? {}),
    locationId: props.locationDetails?.locationId ?? 0,
    orgId: props.locationDetails?.orgId ?? 0,
    baseYear: props.locationDetails?.baseYear ?? new Date().getFullYear(),
    targetYear: props.locationDetails?.targetYear ?? Constants.TargetYear,
    averageAnnualGrowthRate: props.locationDetails?.averageAnnualGrowthRate ?? 0,
  };

  // State variables
  const [loading, setLoading] = useState(false);
  const [locationDetails, setLocationDetails] =
    useState<LocationModel>(initialLocationDetails);
  const [growthOverTargetPeriod, setGrowthOverTargetPeriod] = useState<
    number | undefined
  >();
  const [calculatedAvgAnnualGrowthRate, setCalculatedAvgAnnualGrowthRate] =
    useState<number | undefined>();
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
  const [success, setSuccess] = useState<AlertsProps>({
    show: false,
    severity: "info",
    message: "",
  });
  const [calculationErrorMessage, setCalculationErrorMessage] = useState<
    string | null
  >(null);
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);

  const { ghgEmissionsData, calculationError } = useMemo(() => {
    try {
      const data: BAUGHGEmissions[] = [];
      const baseYear = Number(locationDetails.baseYear);
      const targetYear = Number(locationDetails.targetYear);
      const growthRate = (locationDetails?.averageAnnualGrowthRate ?? 0) / 100;
      const historicalData = locationDetails?.historicalEmissionDatas || [];
      const baseYearHistoricalData = historicalData.find(
        (item) => item.emissionYear === baseYear
      );
      const latestHistoricalBaseline = getLatestHistoricalBaseline(historicalData);
      const projectionBaselineYear = latestHistoricalBaseline?.emissionYear ?? baseYear;
      const projectionBaselineEmissions = latestHistoricalBaseline
        ? getTotalEmissions(latestHistoricalBaseline)
        : Number(props.baselineTotalEmissions);
      const baseYearStartingEmissions = baseYearHistoricalData
        ? getTotalEmissions(baseYearHistoricalData)
        : Number(props.baselineTotalEmissions);

      if (
        !Number.isFinite(baseYear) ||
        !Number.isFinite(targetYear) ||
        targetYear < baseYear
      ) {
        throw new Error("The selected start and end years are not valid.");
      }

      for (let i = 0; i <= targetYear - baseYear; i++) {
        const year = baseYear + i;
        const hist = historicalData.find((h) => h.emissionYear === year);
        let GHGEmissions: number;

        if (hist) {
          GHGEmissions =
            (hist.scope1GHGEmission ?? 0) +
            (hist.scope2GHGEmission ?? 0) +
            (hist.scope3GHGEmission ?? 0);
        } else if (i === 0) {
          GHGEmissions = Number(baseYearStartingEmissions.toFixed(2));
        } else {
          GHGEmissions = calculateLinearProjectedEmissions(
            projectionBaselineEmissions,
            growthRate,
            Math.max(0, year - projectionBaselineYear)
          );
        }

        data.push({
          nzdmId: i + 1,
          year,
          GHGEmissions,
          units: "metric tons CO₂e",
        });
      }

      return { ghgEmissionsData: data, calculationError: null };
    } catch (error) {
      return {
        ghgEmissionsData: [] as BAUGHGEmissions[],
        calculationError: error,
      };
    }
  }, [
    locationDetails.baseYear,
    locationDetails.targetYear,
    props.baselineTotalEmissions,
    locationDetails.averageAnnualGrowthRate,
    locationDetails.historicalEmissionDatas
  ]);

  useEffect(() => {
    if (calculationError) {
      const message = getFriendlyErrorMessage(
        calculationError,
        "We could not calculate the growth rate chart right now. Please review the location data and try again."
      );

      setCalculationErrorMessage(message);
      reportUiError(calculationError, {
        feature: "AddGrowthRate",
        action: "calculate emissions",
        component: "AddGrowthRate",
        route: typeof window !== "undefined" ? window.location.pathname : undefined,
        extra: {
          baseYear: locationDetails.baseYear,
          targetYear: locationDetails.targetYear,
        },
      });
      return;
    }

    setCalculationErrorMessage(null);
  }, [calculationError, locationDetails.baseYear, locationDetails.targetYear]);

  // Transform data for LineChart component
  const transformedData = ghgEmissionsData.map((item) => ({
    year: item.year,
    GHGEmissions: item.GHGEmissions,
  }));

  const labels = transformedData.map((item) => item.year.toString());
  const data = transformedData.map((item) => item.GHGEmissions);

  // Trigger growth calculation when emissions data changes

  useEffect(() => {
    // Calculate growth over the target period
    const calculateGrowthOverTargetPeriod = () => {
      if (!ghgEmissionsData.length) {
        setGrowthOverTargetPeriod(undefined);
        return;
      }

      const lastYearEmissions =
        ghgEmissionsData[ghgEmissionsData.length - 1]?.GHGEmissions;
      const initialYearEmissions = ghgEmissionsData[0]?.GHGEmissions;

      if (
        initialYearEmissions == null ||
        lastYearEmissions == null ||
        !Number.isFinite(initialYearEmissions) ||
        !Number.isFinite(lastYearEmissions) ||
        initialYearEmissions === 0
      ) {
        setGrowthOverTargetPeriod(undefined);
        return;
      }

      const growthOverPeriod = (
        (lastYearEmissions / initialYearEmissions - 1) *
        100
      ).toFixed(2);
      setGrowthOverTargetPeriod(Number(growthOverPeriod));
    };
    calculateGrowthOverTargetPeriod();
  }, [ghgEmissionsData]);

  // Trigger growth rate calculation when relevant data changes
  useEffect(() => {
    // Calculate average annual growth rate
    const calculateAvgAnnualGrowthRate = () => {
      const growthOverTargetPeriodPercentage =
        growthOverTargetPeriod ?? 0 / 100;
      const targetYearInt = Number(locationDetails.targetYear);
      const baseYearInt = Number(locationDetails.baseYear);

      if (
        !isNaN(growthOverTargetPeriodPercentage) &&
        targetYearInt !== baseYearInt
      ) {
        const avgAnnualGrowthRate = (
          growthOverTargetPeriodPercentage /
          (targetYearInt - baseYearInt)
        ).toFixed(2);
        setCalculatedAvgAnnualGrowthRate(Number(avgAnnualGrowthRate));
      }
    };

    calculateAvgAnnualGrowthRate();

    //button enable and disable logic
    const isNumeric =
      !isNaN(locationDetails.averageAnnualGrowthRate as number) &&
      locationDetails.averageAnnualGrowthRate?.toString() !== "" &&
      Number(locationDetails.averageAnnualGrowthRate) <= 100;
    setSaveButtonDisabled(!isNumeric);
  }, [
    growthOverTargetPeriod,
    locationDetails.targetYear,
    locationDetails.baseYear,
    locationDetails.averageAnnualGrowthRate,
  ]);

  /**
   * Handle text input changes
   * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent | SelectChangeEvent<number>} event - The change event
   */
  const handleTextChange = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent
      | SelectChangeEvent<number>
  ) => {
    // Ensure newValue can be negative and numeric
    let newValue = event.target.value.toString(); // Get the raw string value directly from the event

    // Truncate to 2 decimal digits if more are entered
    if (/^-?\d+(\.\d{3,})$/.test(newValue)) {
      newValue = newValue.replace(/(\.\d{2})\d+/, '$1');
    }
  
    // Allow empty value or negative values by using regex to validate the input.
    const isNumeric = /^-?\d+(\.\d{1,2})?$/.test(newValue) && Math.abs(Number(newValue)) <= 100;
  
    setLocationDetails({
      ...locationDetails,
      [event.target.name]: newValue,
    });
  
    // Disable the save button if the value is not numeric or out of bounds
    setSaveButtonDisabled(!isNumeric);
  };
  

  /**
   * Handle select input changes
   * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent | SelectChangeEvent<number>} event - The change event
   */
  const handleSelectChange = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent
      | SelectChangeEvent<number>
  ) => {
    setLocationDetails({
      ...locationDetails,
      [event.target.name]: event.target.value,
    });
  };

  /**
   * Save button click handler
   */
  const onSave = async () => {
    try {
      setLoading(true);
      setSaveErrorMessage(null);
      let updatedLocationDetails = { ...locationDetails };
      // Check if baseYear changed
      if (initialLocationDetails.baseYear !== locationDetails.baseYear) {
        const newBaseYear = Number(locationDetails.baseYear);
        const historicalData = locationDetails.historicalEmissionDatas || [];
        const match = historicalData.find(h => h.emissionYear === newBaseYear);
        if (match) {
          // Update LocationDetails properties from historicalEmissionData
          updatedLocationDetails = {
            ...updatedLocationDetails,
            scope1GHGEmission: match.scope1GHGEmission ?? updatedLocationDetails.scope1GHGEmission,
            scope2GHGEmission: match.scope2GHGEmission ?? updatedLocationDetails.scope2GHGEmission,
            scope3GHGEmission: match.scope3GHGEmission ?? updatedLocationDetails.scope3GHGEmission,
            purchasedGoodsAndServices: match.purchasedGoodsAndServices ?? updatedLocationDetails.purchasedGoodsAndServices,
            capitalGoods: match.capitalGoods ?? updatedLocationDetails.capitalGoods,
            fuelandEnergyRelatedActivities: match.fuelandEnergyRelatedActivities ?? updatedLocationDetails.fuelandEnergyRelatedActivities,
            upstreamTransportationAndDistribution: match.upstreamTransportationAndDistribution ?? updatedLocationDetails.upstreamTransportationAndDistribution,
            wasteGeneratedinOperations: match.wasteGeneratedinOperations ?? updatedLocationDetails.wasteGeneratedinOperations,
            businessTravel: match.businessTravel ?? updatedLocationDetails.businessTravel,
            employeeCommuting: match.employeeCommuting ?? updatedLocationDetails.employeeCommuting,
            upstreamLeasedAssets: match.upstreamLeasedAssets ?? updatedLocationDetails.upstreamLeasedAssets,
            downstreamTransportationAndDistribution: match.downstreamTransportationAndDistribution ?? updatedLocationDetails.downstreamTransportationAndDistribution,
            processingofSoldProducts: match.processingofSoldProducts ?? updatedLocationDetails.processingofSoldProducts,
            useofSoldProducts: match.useofSoldProducts ?? updatedLocationDetails.useofSoldProducts,
            endofLifeTreatmentofSoldProducts: match.endofLifeTreatmentofSoldProducts ?? updatedLocationDetails.endofLifeTreatmentofSoldProducts,
            downstreamLeasedAssets: match.downstreamLeasedAssets ?? updatedLocationDetails.downstreamLeasedAssets,
            franchises: match.franchises ?? updatedLocationDetails.franchises,
            investments: match.investments ?? updatedLocationDetails.investments,
            otherUpstream: match.otherUpstream ?? updatedLocationDetails.otherUpstream,
            otherDownstream: match.otherDownstream ?? updatedLocationDetails.otherDownstream,
          };
        }
      }
      const res = await _updateLocation(updatedLocationDetails);
      if (res) {
        setLoading(false);
        props.fetchLocationDetails();
        props.handleCloseSnackBar({
          show: true,
          severity: "success",
          message: "Data Saved Successfully !",
        });
        props.closeDialog();
      }
    } catch (err) {
      const message = getFriendlyErrorMessage(
        err,
        "We could not save the growth rate changes right now. Please try again."
      );

      setSaveErrorMessage(message);
      reportUiError(err, {
        feature: "AddGrowthRate",
        action: "save",
        component: "AddGrowthRate",
        route: typeof window !== "undefined" ? window.location.pathname : undefined,
        extra: {
          locationId: locationDetails.locationId,
          baseYear: locationDetails.baseYear,
          targetYear: locationDetails.targetYear,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle SnackBar close
   */
  const handleCloseSnackBar = () => {
    setSuccess({ show: false, severity: "info", message: "" });
  };

  // JSX for the component
  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          {(saveErrorMessage || calculationErrorMessage) && (
            <Alert
              severity="error"
              sx={{ mb: 1 }}
              data-testid="growth-rate-error-alert"
            >
              {saveErrorMessage ?? calculationErrorMessage}
            </Alert>
          )}
          {/* Top row: Title and Save button */}
          <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Grid item>
              <Typography variant="body2" sx={{ fontSize: 16, fontWeight: 700, color: '#F37021', mb: 0 }} data-testid="growthRateCalculations">
                Growth Rate Calculations
              </Typography>
            </Grid>
           
          </Grid>
          {/* Controls row */}
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, mb: 2,mt:1 }}>
            <Grid container alignItems="flex-start" justifyContent="space-between">
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }} justifyContent="space-between">
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 400, mr: 1 }} data-testid="averageAnnualGrowthLabel">
                      Average Annual Growth Rate %                      
                    </Typography>
                    <TextField data-testid="averageAnnualGrowthRate"
                      required
                      name="averageAnnualGrowthRate"
                      onChange={(e: any) => {
                        handleTextChange(e);
                      }}
                      value={locationDetails.averageAnnualGrowthRate}
                      sx={{ width: 100, minWidth: 0, ml: 1, fontSize: 12, py: 0.5 }}
                      inputProps={{
                        "data-testid": "averageAnnualGrowthRateValue",
                        inputMode: "numeric",
                        maxLength: 5,
                        style: { textAlign: 'left', fontSize: 12, padding: '4px 6px' }
                      }}
                    />
                    <TitleDefinition content={'The average annual growth rate used for projecting future emissions.'} placement="top" />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 400, mr: 1 }} data-testid="baselineYearLabel">
                      Growth Start Year
                      </Typography>
                    <FormControl sx={{ minWidth: 100, ml: 1 }} size="small">
                      <Select
                        data-testid="baseYearValue"
                        name="baseYear"
                        required
                        value={Number(locationDetails.baseYear) || ""}
                        onChange={(e) => {
                          handleSelectChange(e);
                        }}
                        displayEmpty
                        size="small"
                        sx={{ fontSize: 12, py: 0.5, width: 100 }}
                      >
                        {(() => {
                          const start = Number(locationDetails.startYear);
                          const end = Number(locationDetails.endYear);
                          if (!isNaN(start) && !isNaN(end) && start <= end) {
                            return Array.from({ length: end - start + 1 }, (_, i) => {
                              const year = start + i;
                              return (
                                <MenuItem key={year} value={year} sx={{ fontSize: 12, py: 0.5 }}>
                                  {year}
                                </MenuItem>
                              );
                            });
                          }
                          return null;
                        })()}
                      </Select>
                    </FormControl>
                    <TitleDefinition content={'The year used as the baseline for emissions calculations.'} placement="top" />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 400, mr: 1 }} data-testid="targetYearLabel">
                      Growth End Year
                      </Typography>
                    <FormControl sx={{ minWidth: 100, ml: 1 }} size="small">
                      <Select
                        value={locationDetails.targetYear}
                        required
                        data-testid="targetYear"
                        name="targetYear"
                        onChange={(e) => {
                          handleSelectChange(e);
                        }}
                        displayEmpty
                        size="small"
                        sx={{ fontSize: 12, py: 0.5, width: 100 }}
                      >
                        <MenuItem value="" sx={{ fontSize: 12, py: 0.5 }}>Select Target Year</MenuItem>
                        {targetYears.map((year) => (
                          <MenuItem key={year} value={year} sx={{ fontSize: 12, py: 0.5 }}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TitleDefinition content={'Emissions will not be projected beyond the Target Year entered here. For charts in the dashboard to display through 2050, the Target Year must be 2050.'} placement="top" />
                  </Box>
                </Box>
              </Grid>
                <Grid item xs={8} sx={{ py: 1, mt:1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, height: '100%' }}>
                  <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 400 }} data-testid="baselineTotalEmissionsLabel">
                    Baseline Total Emissions       &nbsp;&nbsp;&nbsp; &nbsp;:&nbsp;<b data-testid="baselineTotalEmissions">{
                        (() => {
                          const baseYear = Number(locationDetails.baseYear);
                          const historicalData = locationDetails.historicalEmissionDatas || [];
                          const match = historicalData.find(h => h.emissionYear === baseYear);
                          let baselineSum = 0;
                          if (match) {
                            baselineSum = (match.scope1GHGEmission ?? 0) + (match.scope2GHGEmission ?? 0) + (match.scope3GHGEmission ?? 0);
                          } else {
                            baselineSum = Number(props.baselineTotalEmissions) || 0;
                          }
                          return `${baselineSum.toLocaleString("en-US")} metric tons CO₂e`;
                        })()
                      }</b>
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 400 }} data-testid="growthOverTargetPeriodLabel">
                    Growth Over Target Period       &nbsp;&nbsp;&nbsp; &nbsp;:<b data-testid="growthOverTargetPeriod"> {growthOverTargetPeriod ? `${growthOverTargetPeriod} %` : '0 %'}</b>
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 400 }} >
                    Average Annual Growth Rate  &nbsp;&nbsp;:<b> {calculatedAvgAnnualGrowthRate ? `${calculatedAvgAnnualGrowthRate} metric tons CO₂e` : '0 metric tons CO₂e'}</b>
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, height: '100%' }}>
                  &nbsp;&nbsp;
                  </Box>
                   <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, height: '100%' }}>
                  &nbsp;&nbsp;
                  </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, height: '100%' }}>
                
                   <Button
                    type="submit"
                    variant="contained"
                    data-testid="btnSave"
                    disabled={saveButtonDisabled}
                    sx={{ background: '#F37021', color: '#fff', fontWeight: 700, borderRadius: 2, px: 2, py: 0.5, fontSize: 13, minHeight: 28, alignSelf: 'flex-end', mb: 2 }}
                    onClick={() => onSave()}
                  >
                    SAVE
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
          
          {/* Business As Usual Emissions Section */}
              <Box >
            <Grid   container alignItems="flex-start" justifyContent="space-between">
               <Grid item xs={5.9} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, mb: 1 }}>
              <Typography
            variant="body2"
            sx={{ fontSize: 14, fontWeight: 700, color: '#718096', mb: 1 }}
            data-testid="businessAsUsualEmissionsLabel"
          >
            Business As Usual Emissions
          </Typography>
          {ghgEmissionsData.length > 0 ? (
            <LineChart labels={labels} data={data} />
          ) : (
            <Box
              sx={{
                minHeight: 240,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
                textAlign: "center",
                px: 2,
              }}
              data-testid="growth-rate-chart-fallback"
            >
              Chart data is unavailable right now.
            </Box>
          )}
            </Grid>
             <Grid item xs={5.9} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, ml: 1 }}>
                <Typography
            variant="body2"
            sx={{ fontSize: 14, fontWeight: 700, color: '#718096', mb: 1 }}
          >
            BAU GHG Emissions
          </Typography>
                <Box
                sx={{
                  border: '0px solid #e0e0e0',
                  borderRadius: 0,
                  p: 0,
                  width: '100%',
                  boxShadow: 'none',
                  '& .MuiTableCell-root': {
                    padding: '0px 4px',
                    fontSize: 12,
                    borderBottom: '1px solid #e0e0e0',
                    minHeight: 'unset',
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    fontWeight: 600,
                    fontSize: 12,
                    padding: '0px 4px',
                    background: '#7B7B7B',
                    color: '#fff',
                    borderBottom: '1px solid #e0e0e0',
                    minHeight: 'unset',
                  }
                }}
              >
                <div style={{ height: 270, overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',  background: '#fff' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }} data-testid="mock-data-table">
                    <thead>
                      <tr style={{ background: '#7B7B7B', color: '#fff', fontWeight: 600, fontSize: 12, textAlign: 'center' }}>
                        <th style={{ padding: '4px 8px', borderBottom: '1px solid #e0e0e0', width: 80, position: 'sticky', top: 0, background: '#7B7B7B', zIndex: 2, textAlign: 'center' }}>Year</th>
                        <th style={{ padding: '4px 8px', borderBottom: '1px solid #e0e0e0', width: 120, position: 'sticky', top: 0, background: '#7B7B7B', zIndex: 2, textAlign: 'center' }}>GHG Emissions</th>
                        <th style={{ padding: '4px 8px', borderBottom: '1px solid #e0e0e0', width: 120, position: 'sticky', top: 0, background: '#7B7B7B', zIndex: 2, textAlign: 'center' }}>Units</th>
                      </tr>
                    </thead>
                  </table>
                  <div style={{ flex: 1, overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                      <tbody>
                        {ghgEmissionsData.map((row, idx) => (
                          <tr key={row.nzdmId} style={{ fontSize: 12, textAlign: 'center' }} data-testid={`emissions-${idx}`}  >
                            <td style={{ padding: '4px 8px', borderBottom: '1px solid #e0e0e0', textAlign: 'center', width: 80 }}>{row.year}</td>
                            <td style={{ padding: '4px 8px', borderBottom: '1px solid #e0e0e0', textAlign: 'center', width: 120 }}>{row.GHGEmissions}</td>
                            <td style={{ padding: '4px 8px', borderBottom: '1px solid #e0e0e0', textAlign: 'center', width: 120 }}>{row.units}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                </Box>
              </Grid>
            </Grid>
            
          </Box>
        </Grid>
      </Grid>
      {loading && <Spinner size={80} data-testid="spinner" />}
      <SnackBar
        show={success.show}
        message={success.message}
        severity={success.severity}
        onclose={handleCloseSnackBar}
      />
    </>
  );
};

export default AddGrowthRate;
